import { useState, useEffect, useCallback } from 'react'
import { Hub } from 'aws-amplify/utils'
import {
  signInWithRedirect,
  signOut as amplifySignOut,
  getCurrentUser,
  fetchAuthSession,
} from 'aws-amplify/auth'

export interface User {
  userId: string
  username: string
  email?: string
  role?: string // "admin" or undefined
  allowedSites?: string[] // e.g. ["sawafuji"]
  allowedApps?: string[] // e.g. ["portal", "api", "monitoring"] or ["*"]
}

interface UseAuthReturn {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  signIn: () => Promise<void>
  signOut: () => Promise<void>
}

function parseJsonClaim(value: unknown): string[] {
  if (!value || typeof value !== 'string') return []
  try {
    const parsed = JSON.parse(value)
    return Array.isArray(parsed) ? parsed : [parsed]
  } catch {
    return []
  }
}

export function useAuth(): UseAuthReturn {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const checkAuth = useCallback(async () => {
    setIsLoading(true)
    try {
      const authUser = await getCurrentUser()
      const session = await fetchAuthSession()
      const payload = session.tokens?.idToken?.payload
      setUser({
        userId: authUser.userId,
        username: authUser.username,
        email: payload?.email as string | undefined,
        role: (payload?.['custom:role'] as string) || undefined,
        allowedSites: parseJsonClaim(payload?.['custom:allowed_sites']),
        allowedApps: parseJsonClaim(payload?.['custom:allowed_apps']),
      })
    } catch {
      setUser(null)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    checkAuth()

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      switch (payload.event) {
        case 'signedIn':
          checkAuth()
          break
        case 'signedOut':
          setUser(null)
          break
        case 'tokenRefresh':
          checkAuth()
          break
      }
    })

    return () => {
      unsubscribe()
    }
  }, [checkAuth])

  const signIn = useCallback(async () => {
    await signInWithRedirect()
  }, [])

  const signOut = useCallback(async () => {
    await amplifySignOut()
    setUser(null)
  }, [])

  return {
    user,
    isAuthenticated: user !== null,
    isLoading,
    signIn,
    signOut,
  }
}
